import type { RootOptions } from 'react-dom/client';
import { captureClientException } from '../shared/capture-client-exception';

/** `error` is typed `unknown` by RootOptions, but React attaches `digest` directly onto the
 * Error instance for a hydration/recoverable error (see react-dom's `makeErrorInfo`, which
 * deprecated the identically-named property on `errorInfo`). Reads it defensively since a
 * recoverable error is not guaranteed to carry one. */
function extractErrorDigest(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'digest' in error) {
    const digest = (error as { digest?: unknown }).digest;
    return typeof digest === 'string' ? digest : undefined;
  }
  return undefined;
}

/**
 * Pass to createRoot / hydrateRoot so React 19 reports uncaught and recoverable errors
 * with component stacks, in addition to default Sentry global handlers.
 */
export function getReactRootSentryOptions(): RootOptions {
  return {
    onUncaughtError: (error, errorInfo) => {
      captureClientException(error, {
        tags: { 'react.root': 'onUncaughtError' },
        extra: { componentStack: errorInfo.componentStack },
      });
    },
    onRecoverableError: (error, errorInfo) => {
      captureClientException(error, {
        tags: { 'react.root': 'onRecoverableError' },
        extra: {
          componentStack: errorInfo.componentStack,
          // React 19.2 (react-dom-client.development.js's makeErrorInfo): `errorInfo.digest` is
          // now a getter that only logs "You are accessing 'digest' from the errorInfo object
          // passed to onRecoverableError..." and returns undefined: the digest moved onto the
          // Error instance itself. Reading it from `errorInfo` (as this used to) printed that
          // console warning on every recoverable error in every environment, dev and prod alike.
          digest: extractErrorDigest(error),
        },
      });
    },
  };
}
